/**
 * Cloudflare Turnstile bot challenge. Enabled only when VITE_TURNSTILE_SITE_KEY is set; without it every
 * function here is a no-op, so local development is unaffected. The backend enforces the same paths
 * (Security:BotProtection:ProtectedPaths) and reads the token from the X-Turnstile-Token header.
 */

const SITE_KEY: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 30000;

export const TURNSTILE_HEADER = 'X-Turnstile-Token';

// Keep in sync with Security:BotProtection:ProtectedPaths on the API.
const PROTECTED_PATHS = [
    '/security/login',
    '/security/registeruser',
    '/security/generaterecovery',
    '/security/resendconfirmationemail',
    '/api/contact',
];

interface TurnstileApi {
    render(container: HTMLElement, options: Record<string, unknown>): string;
    remove(widgetId: string): void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let scriptPromise: Promise<void> | null = null;

export function needsTurnstile(url: string): boolean {
    if (!SITE_KEY) {
        return false;
    }
    let path: string;
    try {
        path = new URL(url, window.location.origin).pathname.toLowerCase();
    } catch {
        return false;
    }
    return PROTECTED_PATHS.some(p => path === p || path.startsWith(p + '/'));
}

function loadScript(): Promise<void> {
    if (window.turnstile) {
        return Promise.resolve();
    }
    if (!scriptPromise) {
        scriptPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error('Turnstile script failed to load'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

/**
 * Returns a fresh single-use token, or null when Turnstile is disabled or could not run (the API then
 * decides: it rejects with 403 when it is enforcing the challenge). "interaction-only" keeps the widget
 * invisible unless Cloudflare needs the user to do something.
 */
export async function getTurnstileToken(): Promise<string | null> {
    if (!SITE_KEY) {
        return null;
    }

    try {
        await loadScript();
    } catch (error) {
        console.log(error);
        return null;
    }

    return new Promise<string | null>(resolve => {
        const container = document.createElement('div');
        container.style.cssText = 'position:fixed;bottom:16px;right:16px;z-index:2147483647;';
        document.body.appendChild(container);

        let widgetId: string | undefined;
        const finish = (token: string | null) => {
            window.clearTimeout(timer);
            if (widgetId !== undefined) {
                try {
                    window.turnstile?.remove(widgetId);
                } catch {
                    // widget already gone
                }
            }
            container.remove();
            resolve(token);
        };
        const timer = window.setTimeout(() => finish(null), TOKEN_TIMEOUT_MS);

        widgetId = window.turnstile!.render(container, {
            sitekey: SITE_KEY,
            appearance: 'interaction-only',
            callback: (token: string) => finish(token),
            'error-callback': () => finish(null),
            'timeout-callback': () => finish(null),
        });
    });
}
